import {
  createContext,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

export type Role = 'Employee' | 'Admin';

interface RoleContextValue {
  role: Role;
  setRole: (next: Role) => void;
  toggleRole: () => void;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

const STORAGE_KEY = 'ifs-role';

export const RoleProvider = ({ children }: PropsWithChildren<{}>) => {
  const [role, setRoleState] = useState<Role>(() => {
    if (typeof window === 'undefined') return 'Admin';
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === 'Admin' || saved === 'Employee'
      ? (saved as Role)
      : 'Admin';
  });

  const setRole = (next: Role) => {
    setRoleState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
      window.location.reload();
    }
  };

  const value = useMemo(
    () => ({
      role,
      setRole,
      toggleRole: () => setRole(role === 'Admin' ? 'Employee' : 'Admin'),
    }),
    [role],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return ctx;
};
